import { observer } from "mobx-react"
import {
  Page,
  Header,
  DocumentTemplate,
  useStore,
  PureLogo,
} from "@features/common"
import { PrivacyLogo } from "@features/privacy"
import { serverSidePagePrepare } from "@lib/hooks"
import { scrollToTop } from "@lib/scroll"
import { RouterLink } from "@lib/routing"
import { IconHamburger } from "@lib/icons"
import {
  DesktopTableOfContents,
  LegalSectionView,
  PreviousVersions,
  TouchTableOfContents,
} from "@features/terms"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { dateFormater } from "@lib/date"
import { redirect } from "@lib/isomorphic"

export const getServerSideProps = serverSidePagePrepare({
  callback: async ({ Models, ctx }) => {
    const { effective_date } = ctx.query
    const queryDate = Array.isArray(effective_date)
      ? effective_date[0]
      : effective_date

    await Models.FaqModel.init()
    try {
      const countryCode = await Models.GlobalModel.getClientCountryCode(ctx)
      await Models.LegalModel.getPrivacyPageData(queryDate, countryCode)
    } catch {
      redirect(`/${Models.GlobalModel.lang}/not-found`, ctx)
    }
  },
})

const PrivacyPage = observer(function PrivacyPage() {
  const {
    GlobalModel: { getTemplate, openMenu },
    LegalModel: {
      privacySections,
      privacyTitle,
      privacyEffectiveDate,
      privacyVersions,
    },
  } = useStore()
  const { asPath } = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  const metaTitle = getTemplate("privacy.metaTitle") || privacyTitle
  const description = getTemplate("privacy.metaDescription")

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    function scrollToAnchor() {
      const hash = asPath.split("#")[1]
      if (hash) {
        const section = document.querySelector(`[data-faq-id=${hash}]`)
        if (section) {
          section.scrollIntoView()
        }
      }
    }
    if (isMounted) {
      setTimeout(() => scrollToAnchor(), 100)
    }
  }, [isMounted, asPath])

  return (
    <Page
      meta={{
        title: privacyTitle,
        metaTitle,
        description,
      }}
      header={
        <Header
          theme="dark"
          logo={
            <RouterLink
              uri="/privacy"
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3 tablet-m:mt-[20px] tablet-m:mb-[18px]"
            >
              <div className="shrink-0 flex items-stretch h-4 tablet-m:h-7">
                <PureLogo className="shrink-0 block h-full w-auto tablet:h-full text-white pr-2 tablet-m:pr-4" />
                <PrivacyLogo />
              </div>
            </RouterLink>
          }
          languageSwitcher={null}
          navigationOpener={
            <button
              onClick={openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
        />
      }
      seoH1={privacyTitle}
    >
      <div className="container pt-3 pb-15 laptop:pt-8 laptop:pb-20 legal-document">
        {privacySections.length > 1 ? (
          <TouchTableOfContents tableOfContents={privacySections} />
        ) : null}
        <DocumentTemplate
          sidebar={
            privacySections.length > 1 ? (
              <DesktopTableOfContents tableOfContents={privacySections} />
            ) : null
          }
        >
          <section className="space-y-10" id="scrollContainerElement">
            {privacySections.map((i) => (
              <LegalSectionView data={i} key={i.id} id={i.id} />
            ))}
            <div>
              <div className="pb-3">
                <strong>Effective date:</strong>{" "}
                {privacyEffectiveDate
                  ? dateFormater(
                      new Date(privacyEffectiveDate),
                      "dd MMMM yyyy",
                      "en",
                    )
                  : ""}
              </div>
              <PreviousVersions versions={privacyVersions} />
            </div>
          </section>
        </DocumentTemplate>
      </div>
    </Page>
  )
})

export default PrivacyPage
